import React, { Fragment, useMemo, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Modal } from '@telus-uds/components-web';

import { Error } from '../../../design/elements';
import FormField from '../../formField';
import Note from '../../../note';
import {
  EquipmentSectionWrapper,
  EquipmentSectionTitle,
  EquipmentSectionSubTitle
} from '../../css';
import { useLocalizedStringsContext } from '../../../context/LocalizedStringsContext';
import stringInterpolation from '../../../utils/stringInterpolation';
import {
  cameraTypeList,
  keyMonitorAdditionalEquipmentVulcan,
  typeSwitch,
  keyProtectAdditionalEquipmentVulcan,
  keyControlAdditionalEquipmentVulcan,
  keyControlIncludedEquipmentCameraSelectionVulcan,
  secureBusinessOppositeSectionKeysVulcan,
  secureBusinessMaxCameraLimit,
  fivePortPoeSwitchVulcan,
  fivePortPoeSwitchPaidVulcan,
  easyFiveYearTemperatureProbeVulcan,
  controlIncludedPoeSwitchVulcan,
  fieldPoeSwitchIncludedEquipment,
  controlIncludedPaid2TbSvrVulcan,
  temperatureSensorVulcan,
  temperatureProbeVulcan,
  easyThreeYearTemperatureSensorVulcan,
  easyThreeYearTemperatureProbeVulcan,
  protectIncludedPoeTurretCameraVulcan,
  controlIncludedPoeTurretCameraVulcan,
  easyFiveYearTemperatureSensorVulcan,
  controlIncludedIndoorCameraVulcan,
  controlIncludedOutdoorCameraVulcan,
  controlIncludedSdCardVulcan,
  indoorCameraVulcan,
  easyThreeYearIndoorCameraVulcan,
  easyFiveYearIndoorCameraVulcan,
  outdoorCameraVulcan,
  easyThreeYearOutdoorCameraVulcan,
  easyFiveYearOutdoorCameraVulcan,
  keyProtectIncludedEquipmentCameraSelectionVulcan,
  smartCameraIncludedEquipmentCameraSelectionVulcan,
  protectIncludedOutdoorCameraVulcan,
  smartCameraIncludedOutdoorCameraVulcan,
  microSdCard128GbVulcan,
  easyThreeYearMicroSdCard128GbVulcan,
  easyFiveYearMicroSdCard128GbVulcan,
  controlIncludedCameraLimit,
  smartCameraIncludedPoeTurretCameraVulcan,
  vulcanForm,
  varifocalCameraVulcan,
  easyThreeYearVarifocalCameraVulcan,
  easyFiveYearVarifocalCameraVulcan,
  temperatureMonitoringFiveDollarVulcan,
  temperatureMonitoringTenDollarVulcan,
  existingEquipmentVulcan,
  existingEquipmentWiredSensors,
  takeoverEquipmentWired,
  powergDoorWindowSensorAuxVulcan,
  easyThreeYearPowergDoorWindowSensorAuxVulcan,
  easyFiveYearPowergDoorWindowSensorAuxVulcan,
  powergDoorWindowSensorVulcan,
  easyThreeYearPowergDoorWindowSensorVulcan,
  easyFiveYearPowergDoorWindowSensorVulcan,
  poeBracketVulcan,
} from '../../../utils/constant';
import { getTotalCamera, updatePoeSwitchVulcanOptionInIncludedSectionTSB, countTemperatureSensorAll, countAllPoeCamerasInSection, syncOneTimePoeSwitchFromCameras, checkIfTotalOverLimit, getTakeoverEquipmentConfigForWiredSensors, getDropdownPricingFromValue, getObjectValue, setObjectValue, getPoeCameraCountInSection, syncPowergContactFromAux, syncPoeBracketFromDomeCamera } from './utils';

const EquipmentSectionForm = ({
  data,
  formErrors = {},
  formFields,
  setFormFields,
  isFormTouched,
  setIsFormTouched,
  renderTempSensor,
  setRenderTempSensor,
  isExpanded,
  setShowDownloadQuoteFooter,
  channelName,
  postalCodeMatches
}) => {
  const [isAlert, setIsAlertOpen] = useState(false);
  const [bodyText, setBodyText] = useState('');
  const [temperatureSensorCountAll, setTemperatureSensorCountAll] = useState(0);

  const {
    name,
    title,
    subTitle,
    note,
    isShowNoteBackground,
    isWarningMessage,
    isColorRed,
    isItalicNote,
    maxValue,
    isLimitCameraOnly,
    formStepFields,
    oneTimePaymentTitle,
    oneTimePaymentTitleVulcan,
    threeYearEasyPaymentTitle,
    fiveYearEasyPaymentTitle,
    easyPaymentTitle,
    easyPaymentFields,
    threeYearEasyPaymentFields,
    fiveYearEasyPaymentFields,
    oneTimePaymentFields
  } = data;

  let finalFields;
  if (Array.isArray(formStepFields) && Array.isArray(easyPaymentFields)) {
    finalFields = [...formStepFields, ...easyPaymentFields];
  } else if (
    Array.isArray(oneTimePaymentFields) &&
    Array.isArray(threeYearEasyPaymentFields) &&
    Array.isArray(fiveYearEasyPaymentFields)
  ) {
    finalFields = [
      ...oneTimePaymentFields,
      ...threeYearEasyPaymentFields,
      ...fiveYearEasyPaymentFields
    ];
  } else {
    finalFields = formStepFields;
  }

  const radioSelection = name.slice(0, 7);
  const localizedStrings = useLocalizedStringsContext();
  const {
    fieldPlusMinus: { errorMessage },
    secureBusiness: { maxCameraLimitErrorMessage }
  } = localizedStrings;

  const checkIfHasSwitchOption = () => {
    const switchOptionFields = [
      fivePortPoeSwitchVulcan,
      fivePortPoeSwitchPaidVulcan,
      fieldPoeSwitchIncludedEquipment,
      temperatureProbeVulcan,
      easyThreeYearTemperatureProbeVulcan,
      easyFiveYearTemperatureProbeVulcan,
      controlIncludedPoeSwitchVulcan,
      'videoServiceCharge',
      'deterrenceAI'
    ];
    const section = formFields[name];
    return section && switchOptionFields.some(field =>
      Object.prototype.hasOwnProperty.call(section, field)
    );
  };

  const formObjectKeys = useMemo(
    () =>
      finalFields.reduce((acc, { fields: { name: formName } }) => {
        acc.push(formName);
        return acc;
      }, []),
    [formStepFields]
  );

  const includedPoeSwitchMapping = {
    control: controlIncludedPoeSwitchVulcan
  };
  const additionalEquipmentPoeCameraFields = [
    protectIncludedPoeTurretCameraVulcan,
    controlIncludedPoeTurretCameraVulcan,
    smartCameraIncludedPoeTurretCameraVulcan
  ];
  const additionalEquipmentTempSensorFields = [
    temperatureSensorVulcan,
    easyThreeYearTemperatureSensorVulcan,
    easyFiveYearTemperatureSensorVulcan
  ];
  const additionalEquipmentKeyByRadio = {
    protect: keyProtectAdditionalEquipmentVulcan,
    control: keyControlAdditionalEquipmentVulcan,
    monitor: keyMonitorAdditionalEquipmentVulcan
  };

  const getControlIncludedCameraTotal = (opt, fieldProductType) => {
    const returnObj = {
      controlIncludedCameraTotalOverLimit: false,
      isControlCameraLimit: false
    };

    if (
      [keyControlIncludedEquipmentCameraSelectionVulcan].includes(name) &&
      cameraTypeList.includes(fieldProductType)
    ) {
      const sectionSum = formObjectKeys
        .filter(k => cameraTypeList.includes(opt[k].productType))
        .reduce((acc, k) => acc + parseInt(opt[k].value || 0, 10), 0);
      if (sectionSum > 2) {
        returnObj.controlIncludedCameraTotalOverLimit = true;
        const text = stringInterpolation(
          returnObj.isControlCameraLimit
            ? maxCameraLimitErrorMessage
            : errorMessage,
          returnObj.isControlCameraLimit
            ? secureBusinessMaxCameraLimit
            : controlIncludedCameraLimit
        );
        setIsAlertOpen(true);
        setBodyText(text);
        return returnObj;
      }
    }
    return returnObj; // Ensure returnObj is always returned
  };

  useEffect(() => {
    setTemperatureSensorCountAll(
      countTemperatureSensorAll(formFields, radioSelection, [
        'oneTimePayment',
        'threeYearEasyPayment',
        'fiveYearEasyPayment'
      ])
    );
  }, [formFields]);

  useEffect(() => {
    // Sync temperature monitoring visibility and values with total sensor count:
    // - No sensors: hide both, set both to 0
    // - 1–5 sensors: show $5 option (value 1), hide $10 (value 0)
    // - 6+ sensors: hide $5 (value 0), show $10 option (value 1)
    const count = temperatureSensorCountAll;
    const noSensors = count < 1 || count === undefined;
    const useFiveDollar = !noSensors && count < 6;
    const useTenDollar = !noSensors && count > 5;

    const fiveDollarState = {
      isVisible: useFiveDollar,
      value: useFiveDollar ? 1 : 0
    };
    const tenDollarState = {
      isVisible: useTenDollar,
      value: useTenDollar ? 1 : 0
    };

    const sectionKeys = [
      keyMonitorAdditionalEquipmentVulcan,
      keyProtectAdditionalEquipmentVulcan,
      keyControlAdditionalEquipmentVulcan
    ];

    const fields = { ...formFields };
    sectionKeys.forEach((sectionKey) => {
      const section = fields[sectionKey];
      if (
        section &&
        section[temperatureMonitoringFiveDollarVulcan] &&
        section[temperatureMonitoringTenDollarVulcan]
      ) {
        section[temperatureMonitoringFiveDollarVulcan].isVisible = fiveDollarState.isVisible;
        section[temperatureMonitoringFiveDollarVulcan].value = fiveDollarState.value;
        section[temperatureMonitoringTenDollarVulcan].isVisible = tenDollarState.isVisible;
        section[temperatureMonitoringTenDollarVulcan].value = tenDollarState.value;
      }
    });
    setRenderTempSensor(!renderTempSensor);
    // Only re-run when sensor count changes; formFields read from closure to avoid overwriting on every form update
  }, [temperatureSensorCountAll]);

  // eslint-disable-next-line consistent-return
  const handleFormField = ({ name: fieldName, value }) => {
    if ([
      controlIncludedSdCardVulcan,
      microSdCard128GbVulcan,
      easyThreeYearMicroSdCard128GbVulcan,
      easyFiveYearMicroSdCard128GbVulcan,
      temperatureProbeVulcan,
      temperatureProbeVulcan,
      easyThreeYearTemperatureProbeVulcan,
      easyFiveYearTemperatureProbeVulcan,
      controlIncludedPaid2TbSvrVulcan,
      // Auto-managed PowerG Door/Window Sensor (Contact) variants — driven by
      // their corresponding Aux fields; user cannot edit directly.
      powergDoorWindowSensorVulcan,
      easyThreeYearPowergDoorWindowSensorVulcan,
      easyFiveYearPowergDoorWindowSensorVulcan,
      // Auto-managed PoE Bracket — driven by the PoE Dome Cameras across all
      // payment tiers (and the included PoE turret for Protect).
      poeBracketVulcan
    ].includes(fieldName)) {
      return null;
    }

    // Check if trying to add a guard response when one already exists
    const guardResponseFields = ['guardResponseWLN', 'guardResponseWLNMonitor'];
    if (guardResponseFields.includes(fieldName)) {
      const newValue = parseInt(value, 10);
      const currentValue = (formFields[name] && formFields[name][fieldName] && formFields[name][fieldName].value) || 0;
      // Only check when incrementing (new value > current value)
      if (newValue > currentValue) {
        // Check if any section already has a guard response with value >= 1
        const hasExistingGuardResponse = Object.values(formFields).some(
          section => section && guardResponseFields.some(
            guardField => section[guardField] && section[guardField].value >= 1
          )
        );
        if (hasExistingGuardResponse) {
          setIsAlertOpen(true);
          setBodyText('Only one guard response can be added per order.');
          return null;
        }
      }
    }

    if (!formFields || !formFields[name]) {
      return null;
    }

    const fields = _.cloneDeep(formFields);

    if (name === existingEquipmentVulcan && fieldName === existingEquipmentWiredSensors) {
      const qty = parseInt(value, 10) || 0;
      const config = getTakeoverEquipmentConfigForWiredSensors(qty);
      const section = fields[takeoverEquipmentWired];
      if (section) {
        Object.keys(config).forEach(key => {
          if (section[key]) {
            section[key].value = config[key].value;
            section[key].minValue = config[key].minValue;
          }
        });
      }
    }

    if (fieldName.includes('_dropdown')) {
      const baseFieldName = fieldName.replace('_dropdown', '');
      const baseField = fields[name][baseFieldName];
      const options = (baseField && baseField.dropdownOptions) || [];
      const pricing = getDropdownPricingFromValue(value, options);
      fields[name][baseFieldName] = {
        ...fields[name][baseFieldName],
        ...pricing,
        dropdownValue: value
      };
    }

    fields[name][fieldName] = {
      ...fields[name][fieldName],
      value: !fieldName.includes('_dropdown') ? parseInt(value, 10) : value
    };

    const poeCameraCountAll = countAllPoeCamerasInSection(
      fields,
      additionalEquipmentKeyByRadio[radioSelection],
      radioSelection
    );

    const setSdCard = (obj) => {
      if (radioSelection === 'control') {
        const controlIncludedPaid2TbSvrVulcanValue = getObjectValue(obj, `${keyControlIncludedEquipmentCameraSelectionVulcan}.controlIncludedPaid2TbSvrVulcan`);
        const cameraSum =
          parseInt(getObjectValue(obj, `${keyControlIncludedEquipmentCameraSelectionVulcan}.controlIncludedIndoorCameraVulcan`), 10) +
          parseInt(getObjectValue(obj, `${keyControlIncludedEquipmentCameraSelectionVulcan}.controlIncludedOutdoorCameraVulcan`), 10);
        if (cameraSum > 0 && !(controlIncludedPaid2TbSvrVulcanValue >= 1)) {
          setObjectValue(obj, `${keyControlIncludedEquipmentCameraSelectionVulcan}.controlIncludedSdCardVulcan`, cameraSum);
        } else if (cameraSum === 0 || controlIncludedPaid2TbSvrVulcanValue >= 1) {
          setObjectValue(obj, `${keyControlIncludedEquipmentCameraSelectionVulcan}.controlIncludedSdCardVulcan`, 0);
        }
      }
    };

    const fieldProductType = fields[name][fieldName].productType;

    const { totalOverLimit } = checkIfTotalOverLimit(fields[name], fieldProductType, {
      formFields,
      sectionName: name,
      formObjectKeys,
      maxValue,
      isLimitCameraOnly,
      maxCameraLimitErrorMessage,
      errorMessage,
      onOverLimit: (message, limitValue) => {
        const text = stringInterpolation(message, limitValue);
        setIsAlertOpen(true);
        setBodyText(text);
      }
    });

    if (radioSelection === 'smartCa' || radioSelection === 'protect') {
      // Add logic for videoServiceCharge and sdCardSmartCamera is for smart camera only
      getTotalCamera(
        fields[name],
        fieldProductType,
        fields,
        name,
        formFields,
        formObjectKeys
      );
    }

    const { controlIncludedCameraTotalOverLimit } = getControlIncludedCameraTotal(fields[name], fieldProductType);

    // Only apply field updates when neither total-over-limit nor control-included camera limit is exceeded
    if (!totalOverLimit && !controlIncludedCameraTotalOverLimit) {
      if (!isFormTouched) {
        setIsFormTouched(true);
      }

      // PowerG Door/Window Sensor with Aux: mirror qty onto the Contact
      // variant (auto-added at $0 with SWT 0.25). Runs for any payment tier.
      syncPowergContactFromAux(fields, fieldName, value, radioSelection);

      // PoE Dome Camera: auto-manage the single one-time PoE Bracket to cover
      // every PoE Dome Camera across all payment tiers (plus the included PoE
      // turret for Protect and Smart Camera). Pricing from Contentful.
      syncPoeBracketFromDomeCamera(fields, fieldName, value, radioSelection);

      // Consolidated one-time PoE switch: every PoE camera across all payment
      // tiers (plus the plan's included PoE turret camera) drives a single pair
      // of one-time 5-port switches in the additional-equipment section.
      syncOneTimePoeSwitchFromCameras(fields, radioSelection);

      // AI Deterrence: show when any Outdoor Camera is selected in the additional section
      // (any payment type) OR in the plan's included camera selection section.
      if (fields[name] && fields[name].deterrenceAI) {
        const additionalSection = fields[name];
        const additionalOutdoorCount =
          parseInt((additionalSection[outdoorCameraVulcan] || {}).value || 0, 10) +
          parseInt((additionalSection[easyThreeYearOutdoorCameraVulcan] || {}).value || 0, 10) +
          parseInt((additionalSection[easyFiveYearOutdoorCameraVulcan] || {}).value || 0, 10);

        let includedOutdoorCount = 0;
        if (radioSelection === 'protect') {
          const includedSection = fields[keyProtectIncludedEquipmentCameraSelectionVulcan] || {};
          includedOutdoorCount = parseInt((includedSection[protectIncludedOutdoorCameraVulcan] || {}).value || 0, 10);
        } else if (radioSelection === 'smartCa') {
          const includedSection = fields[smartCameraIncludedEquipmentCameraSelectionVulcan] || {};
          includedOutdoorCount = parseInt((includedSection[smartCameraIncludedOutdoorCameraVulcan] || {}).value || 0, 10);
        }

        const totalOutdoorCount = additionalOutdoorCount + includedOutdoorCount;
        if (totalOutdoorCount < 1) {
          fields[name].deterrenceAI.isVisible = false;
          fields[name].deterrenceAI.value = 0;
        } else {
          fields[name].deterrenceAI.isVisible = true;
        }
      }

      // --- One-time / non-easy payment: POE switch, SD card, temperature probe, and 2TB SVR logic ---
      if (checkIfHasSwitchOption() && !fieldName.startsWith('easy')) {
        // Count POE cameras in this section (excluding easy payment fields) for switch visibility
        const poeCameraCount = getPoeCameraCountInSection(fields, name, formObjectKeys, k => !k.startsWith('easy'));
        // Find 5-port POE switch field(s) in this section to show/hide based on POE camera count (1–4)
        const switchKey = formObjectKeys.filter(k => {
          return (
            fields[name][k].productType === typeSwitch &&
            fields[name][k].name === fivePortPoeSwitchVulcan
          );
        });

        if (switchKey.length > 0) {
          // One-time PoE switches are managed centrally by
          // syncOneTimePoeSwitchFromCameras; here only the 2TB SVR + SD card
          // are derived from the aggregate POE camera count.
          if (
            fields[keyControlIncludedEquipmentCameraSelectionVulcan] &&
            fields[keyControlIncludedEquipmentCameraSelectionVulcan][controlIncludedPaid2TbSvrVulcan]
          ) {
            fields[keyControlIncludedEquipmentCameraSelectionVulcan][controlIncludedPaid2TbSvrVulcan].value = poeCameraCountAll > 0 ? 1 : 0;
          }
          setSdCard(fields);
        }
        // Map current section to included POE switch field name for visibility sync
        const includedSwitchName = includedPoeSwitchMapping[radioSelection];
        const includedSwitchKey = formObjectKeys.filter(k => {
          return (
            fields[name][k].productType === typeSwitch &&
            fields[name][k].name === includedSwitchName
          );
        });
        // When adding a POE turret in additional equipment, sync included-section POE switch options
        if (
          additionalEquipmentPoeCameraFields.includes(fieldName) &&
          value > 0
        ) {
          updatePoeSwitchVulcanOptionInIncludedSectionTSB(
            radioSelection,
            fields
          );
        }
        // Control section: show/hide and set 2TB SVR when POE turret camera is added or removed
        if (fieldName === controlIncludedPoeTurretCameraVulcan) {
          if (radioSelection === 'control') {
            const controlIncludedSection =
              fields[keyControlIncludedEquipmentCameraSelectionVulcan];
            const svrField =
              controlIncludedSection &&
              controlIncludedSection.controlIncludedPaid2TbSvrVulcan;
            if (svrField) {
              if (value > 0) {
                svrField.value = 1;
                svrField.isVisible = true;
              } else if ((value === 0 || value !== 0) && poeCameraCountAll === 0) {
                svrField.value = 0;
              }
            }
          }
        }
        // Included control: set SD card qty to indoor+outdoor camera sum when no 2TB SVR; else 0

        if (
          [
            controlIncludedIndoorCameraVulcan,
            controlIncludedOutdoorCameraVulcan,
            controlIncludedPoeTurretCameraVulcan
          ].includes(fieldName)
        ) {
          const controlIncludedPoeTurretCameraValue = getObjectValue(fields, `${keyControlIncludedEquipmentCameraSelectionVulcan}.controlIncludedPaid2TbSvrVulcan`);
          if (radioSelection === 'control') {
            const cameraSum =
              parseInt(getObjectValue(fields, `${keyControlIncludedEquipmentCameraSelectionVulcan}.controlIncludedIndoorCameraVulcan`), 10) +
              parseInt(getObjectValue(fields, `${keyControlIncludedEquipmentCameraSelectionVulcan}.controlIncludedOutdoorCameraVulcan`), 10);
            if (cameraSum > 0 && !(controlIncludedPoeTurretCameraValue >= 1)) {
              setObjectValue(fields, `${keyControlIncludedEquipmentCameraSelectionVulcan}.controlIncludedSdCardVulcan`, cameraSum);
            } else if (cameraSum === 0 || controlIncludedPoeTurretCameraValue >= 1) {
              setObjectValue(fields, `${keyControlIncludedEquipmentCameraSelectionVulcan}.controlIncludedSdCardVulcan`, 0);
            }
          }
        }

        // Additional control: set micro SD card 128GB to indoor+outdoor sum when no 2TB SVR
        if ([
            indoorCameraVulcan,
            outdoorCameraVulcan,
            controlIncludedPoeTurretCameraVulcan
          ].includes(fieldName)) {
          const controlIncludedPoeTurretCameraValue = getObjectValue(fields, `${keyControlIncludedEquipmentCameraSelectionVulcan}.controlIncludedPaid2TbSvrVulcan`);
          if (radioSelection === 'control') {
            const cameraSum =
              parseInt(getObjectValue(fields, `${keyControlAdditionalEquipmentVulcan}.indoorCameraVulcan`), 10) +
              parseInt(getObjectValue(fields, `${keyControlAdditionalEquipmentVulcan}.outdoorCameraVulcan`), 10);
            if (cameraSum > 0 && !(controlIncludedPoeTurretCameraValue >= 1)) {
              setObjectValue(fields, `${keyControlAdditionalEquipmentVulcan}.microSdCard128GbVulcan`, cameraSum);
            } else if (cameraSum === 0 || controlIncludedPoeTurretCameraValue >= 1) {
              setObjectValue(fields, `${keyControlAdditionalEquipmentVulcan}.microSdCard128GbVulcan`, 0);
            }
          }
        }

        // Additional control (5-year easy): set easy five year micro SD to indoor+outdoor sum when no 2TB SVR
        if ([
            easyFiveYearIndoorCameraVulcan,
            easyFiveYearOutdoorCameraVulcan,
            controlIncludedPoeTurretCameraVulcan
          ].includes(fieldName)) {
          const controlIncludedPoeTurretCameraValue = getObjectValue(fields, `${keyControlIncludedEquipmentCameraSelectionVulcan}.controlIncludedPaid2TbSvrVulcan`);

          if (radioSelection === 'control') {
            const cameraSum =
              parseInt(getObjectValue(fields, `${keyControlAdditionalEquipmentVulcan}.easyFiveYearIndoorCameraVulcan`), 10) +
              parseInt(getObjectValue(fields, `${keyControlAdditionalEquipmentVulcan}.easyFiveYearOutdoorCameraVulcan`), 10);
            if (cameraSum > 0 && !(controlIncludedPoeTurretCameraValue >= 1)) {
              setObjectValue(fields, `${keyControlAdditionalEquipmentVulcan}.easyFiveYearMicroSdCard128GbVulcan`, cameraSum);
            } else if (cameraSum === 0 || controlIncludedPoeTurretCameraValue >= 1) {
              setObjectValue(fields, `${keyControlAdditionalEquipmentVulcan}.easyFiveYearMicroSdCard128GbVulcan`, 0);
            }
          }
        }

        // One-time: show temperature probe and set value when sensor added; hide and zero when removed
        if (additionalEquipmentTempSensorFields.includes(fieldName)) {
          const sectionKey = additionalEquipmentKeyByRadio[radioSelection];
          if (sectionKey) {
            if (value > 0) {
              setObjectValue(fields, `${sectionKey}.temperatureProbeVulcan`, true, 'isVisible');
              setObjectValue(fields, `${sectionKey}.temperatureProbeVulcan`, value);
            } else if (value < 1) {
              setObjectValue(fields, `${sectionKey}.temperatureProbeVulcan`, false, 'isVisible');
              setObjectValue(fields, `${sectionKey}.temperatureProbeVulcan`, 0);
            }
          }
        }
        // Sync included POE switch visibility and value with whether any POE cameras are present
        if (includedSwitchKey.length > 0) {
          for (let i = 0; i < includedSwitchKey.length; i += 1) {
            fields[name][includedSwitchKey[i]].isVisible = poeCameraCount > 0;
            fields[name][includedSwitchKey[i]].value = poeCameraCount > 0 ? 1 : 0;
          }
        }
      }
      // --- Three-year easy payment: SD card, POE switch, temperature probe, 2TB SVR ---
      else if (
        checkIfHasSwitchOption() &&
        fieldName.startsWith('easyThreeYear')
      ) {
        // Sync 3-year micro SD card with indoor+outdoor when no 2TB SVR (control section)
        if (
          [
            easyThreeYearIndoorCameraVulcan,
            easyThreeYearOutdoorCameraVulcan,
            controlIncludedPoeTurretCameraVulcan
          ].includes(fieldName)
        ) {
          const controlIncludedPoeTurretCameraValue = getObjectValue(
            fields,
            `${keyControlIncludedEquipmentCameraSelectionVulcan}.controlIncludedPaid2TbSvrVulcan`
          );

          if (radioSelection === 'control') {
            const controlAdditionalSection =
              fields[keyControlAdditionalEquipmentVulcan];
            const sdCardField =
              controlAdditionalSection &&
              controlAdditionalSection.easyThreeYearMicroSdCard128GbVulcan;
            const cameraSum =
              parseInt(
                getObjectValue(
                  fields,
                  `${keyControlAdditionalEquipmentVulcan}.easyThreeYearIndoorCameraVulcan`
                ) || 0,
                10
              ) +
              parseInt(
                getObjectValue(
                  fields,
                  `${keyControlAdditionalEquipmentVulcan}.easyThreeYearOutdoorCameraVulcan`
                ) || 0,
                10
              );
            if (sdCardField) {
              if (cameraSum > 0 && !(controlIncludedPoeTurretCameraValue >= 1)) {
                sdCardField.value = cameraSum;
              } else if (
                cameraSum === 0 ||
                controlIncludedPoeTurretCameraValue >= 1
              ) {
                sdCardField.value = 0;
              }
            }
          }
        }
        // Count 3-year easy POE cameras for switch visibility
        const poeCameraCount = getPoeCameraCountInSection(fields, name, formObjectKeys, k => k.startsWith('easyThreeYear'));
        const includedSwitchName = includedPoeSwitchMapping[radioSelection];
        const switchKey = formObjectKeys.filter(k => {
          return (
            fields[name][k].productType === typeSwitch &&
            fields[name][k].name === fivePortPoeSwitchVulcan
          );
        });
        const includedSwitchKey = formObjectKeys.filter(k => {
          return (
            fields[name][k].productType === typeSwitch &&
            fields[name][k].name === includedSwitchName
          );
        });
        if (
          additionalEquipmentPoeCameraFields.includes(fieldName) &&
          value > 0
        ) {
          updatePoeSwitchVulcanOptionInIncludedSectionTSB(
            radioSelection,
            fields
          );
        }
        // 3-year easy: show/set temperature probe from temperature sensor value
        if (additionalEquipmentTempSensorFields.includes(fieldName)) {
          // Handle three-year temperature probes
          if (fieldName === 'easyThreeYearTemperatureSensorVulcan') {
            const key = additionalEquipmentKeyByRadio[radioSelection];
            const isVisible = value > 0;

            if (key && fields[key] && fields[key].easyThreeYearTemperatureProbeVulcan) {
              const probeField = fields[key].easyThreeYearTemperatureProbeVulcan;
              probeField.isVisible = isVisible;
              probeField.value = isVisible ? value : 0;
            }
          }

          // Handle five-year temperature probes
          if (fieldName === 'easyFiveYearTemperatureSensorVulcan') {
            const key = additionalEquipmentKeyByRadio[radioSelection];

            if (key && fields[key] && fields[key].easyFiveYearTemperatureProbeVulcan) {
              const probeField = fields[key].easyFiveYearTemperatureProbeVulcan;
              const isVisible = value > 0;

              probeField.isVisible = isVisible;
              probeField.value = isVisible ? value : 0;
            }
          }
        }

        // 3-year easy: PoE switches are managed centrally by
        // syncOneTimePoeSwitchFromCameras; here only the 2TB SVR + SD card are
        // derived from the aggregate POE camera count.
        if (switchKey.length > 0) {
          if (
            fields[keyControlIncludedEquipmentCameraSelectionVulcan] &&
            fields[keyControlIncludedEquipmentCameraSelectionVulcan][controlIncludedPaid2TbSvrVulcan]
          ) {
            fields[keyControlIncludedEquipmentCameraSelectionVulcan][controlIncludedPaid2TbSvrVulcan].value = poeCameraCountAll > 0 ? 1 : 0;
          }
          setSdCard(fields);
        }
        if (includedSwitchKey.length > 0) {
          for (let i = 0; i < includedSwitchKey.length; i += 1) {
            fields[name][includedSwitchKey[i]].isVisible = poeCameraCount > 0;
            fields[name][includedSwitchKey[i]].value = poeCameraCount > 0 ? 1 : 0;
          }
        }
      }
      // --- Five-year easy payment: SD card, POE switch, temperature probe, 2TB SVR ---
      else if (
        checkIfHasSwitchOption() &&
        fieldName.startsWith('easyFiveYear')
      ) {
        // Count 5-year easy POE cameras for switch visibility
        const poeCameraCount = getPoeCameraCountInSection(fields, name, formObjectKeys, k => k.startsWith('easyFiveYear'));
        // 5-year easy: sync micro SD card with indoor+outdoor when no 2TB SVR (control section)
        if (
          [
            easyFiveYearIndoorCameraVulcan,
            easyFiveYearOutdoorCameraVulcan,
            controlIncludedPoeTurretCameraVulcan
          ].includes(fieldName)
        ) {
          const controlIncludedPoeTurretCameraValue = getObjectValue(
            fields,
            `${keyControlIncludedEquipmentCameraSelectionVulcan}.controlIncludedPaid2TbSvrVulcan`
          );

          if (radioSelection === 'control') {
            const controlAdditionalSection =
              fields[keyControlAdditionalEquipmentVulcan];
            const sdCardField =
              controlAdditionalSection &&
              controlAdditionalSection.easyFiveYearMicroSdCard128GbVulcan;
            const cameraSum =
              parseInt(
                getObjectValue(
                  fields,
                  `${keyControlAdditionalEquipmentVulcan}.easyFiveYearIndoorCameraVulcan`
                ) || 0,
                10
              ) +
              parseInt(
                getObjectValue(
                  fields,
                  `${keyControlAdditionalEquipmentVulcan}.easyFiveYearOutdoorCameraVulcan`
                ) || 0,
                10
              );
            if (sdCardField) {
              if (cameraSum > 0 && !(controlIncludedPoeTurretCameraValue >= 1)) {
                sdCardField.value = cameraSum;
              } else if (
                cameraSum === 0 ||
                controlIncludedPoeTurretCameraValue >= 1
              ) {
                sdCardField.value = 0;
              }
            }
          }
        }
        const includedSwitchName = includedPoeSwitchMapping[radioSelection];
        const switchKey = formObjectKeys.filter(
          k =>
            fields[name][k].productType === typeSwitch &&
            fields[name][k].name === fivePortPoeSwitchVulcan
        );
        const includedSwitchKey = formObjectKeys.filter(k => {
          return (
            fields[name][k].productType === typeSwitch &&
            fields[name][k].name === includedSwitchName
          );
        });
        if (
          additionalEquipmentPoeCameraFields.includes(fieldName) &&
          value > 0
        ) {
          updatePoeSwitchVulcanOptionInIncludedSectionTSB(
            radioSelection,
            fields
          );
        }
        // 5-year easy: show/set temperature probe from 3-year or 5-year temperature sensor value
        if (additionalEquipmentTempSensorFields.includes(fieldName)) {
          // Handle three-year temperature probes
          if (fieldName === 'easyThreeYearTemperatureSensorVulcan') {
            const key = additionalEquipmentKeyByRadio[radioSelection];
            const isVisible = value > 0;

            if (key && fields[key] && fields[key].easyThreeYearTemperatureProbeVulcan) {
              const probeField = fields[key].easyThreeYearTemperatureProbeVulcan;
              probeField.isVisible = isVisible;
              probeField.value = isVisible ? value : 0;
            }
          }

          // Handle five-year temperature probes
          if (fieldName === 'easyFiveYearTemperatureSensorVulcan') {
            const key = additionalEquipmentKeyByRadio[radioSelection];
            if (key && fields[key] && fields[key].easyFiveYearTemperatureProbeVulcan) {
              const probeField = fields[key].easyFiveYearTemperatureProbeVulcan;
              const isVisible = value > 0;

              probeField.isVisible = isVisible;
              probeField.value = isVisible ? value : 0;
            }
          }
        }

        // 5-year easy: PoE switches are managed centrally by
        // syncOneTimePoeSwitchFromCameras; here only the 2TB SVR + SD card are
        // derived from the aggregate POE camera count.
        if (switchKey.length > 0) {
          if (
            fields[keyControlIncludedEquipmentCameraSelectionVulcan] &&
            fields[keyControlIncludedEquipmentCameraSelectionVulcan][controlIncludedPaid2TbSvrVulcan]
          ) {
            fields[keyControlIncludedEquipmentCameraSelectionVulcan][controlIncludedPaid2TbSvrVulcan].value = poeCameraCountAll > 0 ? 1 : 0;
          }
          setSdCard(fields);
        }
        if (includedSwitchKey.length > 0) {
          for (let i = 0; i < includedSwitchKey.length; i += 1) {
            fields[name][includedSwitchKey[i]].isVisible = poeCameraCount > 0;
            fields[name][includedSwitchKey[i]].value = poeCameraCount > 0 ? 1 : 0;
          }
        }
      }

      // Mark form as touched and persist updated fields
      if (!isFormTouched) { 
        setIsFormTouched(true);
        setShowDownloadQuoteFooter(false);
      }
      setFormFields(fields);
    }
  };

  // Single-key wrapper: returns temperature sensor count for one payment type (delegates to countTemperatureSensorAll).
  const countTemperatureSensor = (key) =>
    countTemperatureSensorAll(formFields, radioSelection, [key]);

  const history = useHistory();

  const currentPath = history.location.pathname.split('/')[3];

  return (
    <>
      {title && <EquipmentSectionTitle>{title}</EquipmentSectionTitle>}
      {subTitle && <EquipmentSectionSubTitle>{subTitle}</EquipmentSectionSubTitle>}
      {note && note.content && (
        <Note
          data={note}
          type="richText"
          isShowNoteBackground={isShowNoteBackground}
          isWarningMessage={isWarningMessage}
          isColorRed={isColorRed}
          isItalicNote={isItalicNote}
        />
      )}
      <div style={{ display: isExpanded ? 'block' : 'none', width: '100%', maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', gap: '24px' }}>
        <div style={{ display: 'flex', flexWrap: 'nowrap', width: 'max-content', gap: '50px' }}>
        {formStepFields && (
          <EquipmentSectionWrapper>
            {oneTimePaymentTitle && (
              <EquipmentSectionSubTitle>
                {oneTimePaymentTitle}
              </EquipmentSectionSubTitle>
            )}
            {formFields[name] &&
              formStepFields &&
              formStepFields.map(({ fields, sys: { id: sysId } }, index) => {
                const key = `EquipmentSection${name}${index}`;
                const error = formErrors[name] && formErrors[name][fields.name];

                // These visibility checks must run BEFORE hiddenValue is computed so isVisible is correct

                // Check postal code and set visibility for guardResponseWLN fields
                if (fields.name === 'guardResponseWLN' || fields.name === 'guardResponseWLNMonitor') {
                  if (formFields[name] && formFields[name][fields.name]) {
                    formFields[name][fields.name].isVisible = postalCodeMatches;
                  }
                }

                // Temperature monitoring: hide if no temperature sensors selected
                if (fields.name === 'temperatureMonitoringFiveDollarVulcan' || fields.name === 'temperatureMonitoringTenDollarVulcan') {
                  if (formFields[name] && formFields[name][fields.name] && temperatureSensorCountAll < 1) {
                    formFields[name][fields.name].isVisible = false;
                  }
                }

                // Video service charge: hide if total cameras < 5
                if (fields.name === 'videoServiceCharge') {
                  if (formFields[name] && formFields[name][fields.name]) {
                    const otherSectionKey = secureBusinessOppositeSectionKeysVulcan[name];
                    const currentSection = formFields[name] || {};
                    const otherSection = formFields[otherSectionKey] || {};
                    const currentCameraCount = Object.keys(currentSection)
                      .filter(k => cameraTypeList.includes((currentSection[k] || {}).productType))
                      .reduce((acc, k) => acc + parseInt(currentSection[k].value || 0, 10), 0);
                    const otherCameraCount = Object.keys(otherSection)
                      .filter(k => cameraTypeList.includes((otherSection[k] || {}).productType))
                      .reduce((acc, k) => acc + parseInt(otherSection[k].value || 0, 10), 0);
                    if (currentCameraCount + otherCameraCount < 5) {
                      formFields[name][fields.name].isVisible = false;
                    }
                  }
                }

                // AI Deterrence: show when any Outdoor Camera is selected in the additional
                // section (any payment type) OR in the plan's included camera selection section.
                if (fields.name === 'deterrenceAI') {
                  if (formFields[name] && formFields[name][fields.name]) {
                    const additionalSection = formFields[name];
                    const additionalOutdoorCount =
                      parseInt((additionalSection[outdoorCameraVulcan] || {}).value || 0, 10) +
                      parseInt((additionalSection[easyThreeYearOutdoorCameraVulcan] || {}).value || 0, 10) +
                      parseInt((additionalSection[easyFiveYearOutdoorCameraVulcan] || {}).value || 0, 10);

                    let includedOutdoorCount = 0;
                    if (radioSelection === 'protect') {
                      const includedSection = formFields[keyProtectIncludedEquipmentCameraSelectionVulcan] || {};
                      includedOutdoorCount = parseInt((includedSection[protectIncludedOutdoorCameraVulcan] || {}).value || 0, 10);
                    } else if (radioSelection === 'smartCa') {
                      const includedSection = formFields[smartCameraIncludedEquipmentCameraSelectionVulcan] || {};
                      includedOutdoorCount = parseInt((includedSection[smartCameraIncludedOutdoorCameraVulcan] || {}).value || 0, 10);
                    }

                    const totalOutdoorCount = additionalOutdoorCount + includedOutdoorCount;
                    formFields[name][fields.name].isVisible = totalOutdoorCount > 0;
                    if (totalOutdoorCount < 1) {
                      formFields[name][fields.name].value = 0;
                    }
                  }
                }

                const currentField = formFields[name][fields.name];
                const poeSwitchFields = [
                  fivePortPoeSwitchVulcan,
                  fivePortPoeSwitchPaidVulcan
                ];
                const isPoeSwitchToHide = poeSwitchFields.includes(fields.name) &&
                  currentField &&
                  !currentField.value;

                const hiddenValue = checkIfHasSwitchOption()
                  ? !currentField ||
                    !currentField.isVisible ||
                    currentField.isHidden ||
                    isPoeSwitchToHide
                  : false;

                // SOF-1582: Non-zero value for POE switch should not be visible
                const poeCameraCount = countAllPoeCamerasInSection(formFields, name, radioSelection);
                if (
                  currentPath === 'Vulcan_Form' &&
                  formFields[name][fivePortPoeSwitchPaidVulcan] &&
                  poeCameraCount < 5
                ) {
                  formFields[name][
                    fivePortPoeSwitchPaidVulcan
                  ].isVisible = false;
                }

                const temperatureSensorCount = countTemperatureSensor(
                  'oneTimePayment'
                );
                if (
                  currentPath === vulcanForm &&
                  formFields[name][temperatureProbeVulcan] &&
                  temperatureSensorCount < 1
                ) {
                  formFields[name][temperatureProbeVulcan].isVisible = false;
                }

                // PowerG Door/Window Sensor (Contact): hide unless matching Aux qty > 0
                if (
                  formFields[name][powergDoorWindowSensorVulcan] &&
                  !(
                    formFields[name][powergDoorWindowSensorAuxVulcan] &&
                    parseInt(
                      formFields[name][powergDoorWindowSensorAuxVulcan]
                        .value || 0,
                      10
                    ) > 0
                  )
                ) {
                  formFields[name][
                    powergDoorWindowSensorVulcan
                  ].isVisible = false;
                }

                // PoE Bracket: hide unless its auto-managed qty (PoE Dome
                // Cameras across all tiers + Protect included turret) > 0
                if (
                  formFields[name][poeBracketVulcan] &&
                  !parseInt(formFields[name][poeBracketVulcan].value || 0, 10)
                ) {
                  formFields[name][poeBracketVulcan].isVisible = false;
                }

                const fieldData = {
                  ...fields,
                  ...formFields[name][fields.name],
                  isHidden: hiddenValue,
                  formFields,
                  sysId,
                  onChange: e => handleFormField(e.target)
                };
                return (
                  <Fragment key={key}>
                    <FormField data={fieldData} />
                    {error && error.text && <Error text={error.text} />}
                  </Fragment>
                );
              })}
          </EquipmentSectionWrapper>
        )}

        {/* 
        Easy Payments are shown/hidden based on boolean value of shouldHideEasyPayments
      */}
        {easyPaymentFields && (
          <EquipmentSectionWrapper>
            {easyPaymentTitle && (
              <EquipmentSectionSubTitle>
                {easyPaymentTitle}
              </EquipmentSectionSubTitle>
            )}
            {formFields[name] &&
              Array.isArray(easyPaymentFields) &&
              easyPaymentFields.map(({ fields, sys: { id: sysId } }, index) => {
                const key = `EquipmentSection${name}${index}`;
                const error = formErrors[name] && formErrors[name][fields.name];

                const currentField = formFields[name][fields.name];
                const hiddenValue = checkIfHasSwitchOption()
                  ? !currentField ||
                    !currentField.isVisible ||
                    currentField.isHidden
                  : false;
                const temperatureSensorCount = countTemperatureSensor(
                  'easyPayment'
                );
                if (
                  currentPath === vulcanForm &&
                  formFields[name][easyThreeYearTemperatureProbeVulcan] &&
                  temperatureSensorCount < 1
                ) {
                  formFields[name][
                    easyThreeYearTemperatureProbeVulcan
                  ].isVisible = false;
                }

                // PowerG Door/Window Sensor (Contact, 3Y): hide unless matching Aux qty > 0
                if (
                  formFields[name][
                    easyThreeYearPowergDoorWindowSensorVulcan
                  ] &&
                  !(
                    formFields[name][
                      easyThreeYearPowergDoorWindowSensorAuxVulcan
                    ] &&
                    parseInt(
                      formFields[name][
                        easyThreeYearPowergDoorWindowSensorAuxVulcan
                      ].value || 0,
                      10
                    ) > 0
                  )
                ) {
                  formFields[name][
                    easyThreeYearPowergDoorWindowSensorVulcan
                  ].isVisible = false;
                }

                const fieldData = {
                  ...fields,
                  ...formFields[name][fields.name],
                  isHidden: hiddenValue,
                  formFields,
                  sysId,
                  onChange: e => handleFormField(e.target)
                };
                return (
                  <Fragment key={key}>
                    <FormField data={fieldData} />
                    {error && error.text && <Error text={error.text} />}
                  </Fragment>
                );
              })}
          </EquipmentSectionWrapper>
        )}

        {/* Showing one time payment fields */}
        {oneTimePaymentFields && (
          <EquipmentSectionWrapper>
            {oneTimePaymentTitleVulcan && (
              <EquipmentSectionSubTitle>
                {oneTimePaymentTitleVulcan}
              </EquipmentSectionSubTitle>
            )}
            {formFields[name] &&
              Array.isArray(oneTimePaymentFields) &&
              oneTimePaymentFields.map(
                ({ fields, sys: { id: sysId } }, index) => {
                  const key = `EquipmentSection${name}${index}`;
                  const error =
                    formErrors[name] && formErrors[name][fields.name];

                  // These visibility checks must run BEFORE hiddenValue is computed so isVisible is correct

                  // Check postal code and set visibility for guardResponseWLN fields
                  if (fields.name === 'guardResponseWLN' || fields.name === 'guardResponseWLNMonitor') {
                    if (formFields[name] && formFields[name][fields.name]) {
                      formFields[name][fields.name].isVisible = postalCodeMatches;
                    }
                  }

                  // Temperature monitoring: hide if no temperature sensors selected
                  if (fields.name === 'temperatureMonitoringFiveDollarVulcan' || fields.name === 'temperatureMonitoringTenDollarVulcan') {
                    if (formFields[name] && formFields[name][fields.name] && temperatureSensorCountAll < 1) {
                      formFields[name][fields.name].isVisible = false;
                    }
                  }

                  // Video service charge: hide if total cameras < 5
                  if (fields.name === 'videoServiceCharge') {
                    if (formFields[name] && formFields[name][fields.name]) {
                      const otherSectionKey = secureBusinessOppositeSectionKeysVulcan[name];
                      const currentSection = formFields[name] || {};
                      const otherSection = formFields[otherSectionKey] || {};
                      const currentCameraCount = Object.keys(currentSection)
                        .filter(k => cameraTypeList.includes((currentSection[k] || {}).productType))
                        .reduce((acc, k) => acc + parseInt(currentSection[k].value || 0, 10), 0);
                      const otherCameraCount = Object.keys(otherSection)
                        .filter(k => cameraTypeList.includes((otherSection[k] || {}).productType))
                        .reduce((acc, k) => acc + parseInt(otherSection[k].value || 0, 10), 0);
                      if (currentCameraCount + otherCameraCount < 5) {
                        formFields[name][fields.name].isVisible = false;
                      }
                    }
                  }

                  // AI Deterrence: show when any Outdoor Camera is selected in the additional
                  // section (any payment type) OR in the plan's included camera selection section.
                  if (fields.name === 'deterrenceAI') {
                    if (formFields[name] && formFields[name][fields.name]) {
                      const additionalSection = formFields[name];
                      const additionalOutdoorCount =
                        parseInt((additionalSection[outdoorCameraVulcan] || {}).value || 0, 10) +
                        parseInt((additionalSection[easyThreeYearOutdoorCameraVulcan] || {}).value || 0, 10) +
                        parseInt((additionalSection[easyFiveYearOutdoorCameraVulcan] || {}).value || 0, 10);

                      let includedOutdoorCount = 0;
                      if (radioSelection === 'protect') {
                        const includedSection = formFields[keyProtectIncludedEquipmentCameraSelectionVulcan] || {};
                        includedOutdoorCount = parseInt((includedSection[protectIncludedOutdoorCameraVulcan] || {}).value || 0, 10);
                      } else if (radioSelection === 'smartCa') {
                        const includedSection = formFields[smartCameraIncludedEquipmentCameraSelectionVulcan] || {};
                        includedOutdoorCount = parseInt((includedSection[smartCameraIncludedOutdoorCameraVulcan] || {}).value || 0, 10);
                      }

                      const totalOutdoorCount = additionalOutdoorCount + includedOutdoorCount;
                      formFields[name][fields.name].isVisible = totalOutdoorCount > 0;
                      if (totalOutdoorCount < 1) {
                        formFields[name][fields.name].value = 0;
                      }
                    }
                  }

                  // Must run BEFORE currentField/hiddenValue are computed so
                  // isVisible reflects this render, not the next one.
                  if (fields && fields.name === varifocalCameraVulcan) {
                    if (channelName !== 'SMB Growth Sales CBS' && formFields[name][varifocalCameraVulcan]) {
                      formFields[name][varifocalCameraVulcan].isVisible = false;
                    }
                  }

                  const currentField = formFields[name][fields.name];
                  const poeSwitchFields = [
                    fivePortPoeSwitchVulcan,
                    fivePortPoeSwitchPaidVulcan
                  ];
                  const isPoeSwitchToHide = poeSwitchFields.includes(fields.name) &&
                    currentField &&
                    !currentField.value;

                  const hiddenValue = checkIfHasSwitchOption()
                    ? !currentField ||
                      !currentField.isVisible ||
                      currentField.isHidden ||
                      isPoeSwitchToHide
                    : false;
                  // SOF-1582: Non-zero value for POE switch should not be visible
                  const poeCameraCount = countAllPoeCamerasInSection(formFields, name, radioSelection);
                  if (
                    currentPath === vulcanForm &&
                    fields.name === fivePortPoeSwitchPaidVulcan &&
                    formFields[name][fivePortPoeSwitchPaidVulcan] &&
                    poeCameraCount < 5
                  ) {
                    formFields[name][
                      fivePortPoeSwitchPaidVulcan
                    ].isVisible = false;
                  }
                  const temperatureSensorCount = countTemperatureSensor(
                    'oneTimePayment'
                  );
                  if (
                    currentPath === vulcanForm &&
                    formFields[name][temperatureProbeVulcan] &&
                    temperatureSensorCount < 1
                  ) {
                    formFields[name][temperatureProbeVulcan].isVisible = false;
                  }

                  // PowerG Door/Window Sensor (Contact): hide unless matching Aux qty > 0
                  if (
                    formFields[name][powergDoorWindowSensorVulcan] &&
                    !(
                      formFields[name][powergDoorWindowSensorAuxVulcan] &&
                      parseInt(
                        formFields[name][powergDoorWindowSensorAuxVulcan]
                          .value || 0,
                        10
                      ) > 0
                    )
                  ) {
                    formFields[name][
                      powergDoorWindowSensorVulcan
                    ].isVisible = false;
                  }

                  // PoE Bracket: hide unless its auto-managed qty (PoE Dome
                  // Cameras across all tiers + Protect included turret) > 0
                  if (
                    formFields[name][poeBracketVulcan] &&
                    !parseInt(formFields[name][poeBracketVulcan].value || 0, 10)
                  ) {
                    formFields[name][poeBracketVulcan].isVisible = false;
                  }

                  const fieldData = {
                    ...fields,
                    ...formFields[name][fields.name],
                    isHidden: hiddenValue,
                    formFields,
                    sysId,
                    onChange: e => handleFormField(e.target)
                  };
                  return (
                    <Fragment key={key}>
                      <FormField data={fieldData} />
                      {error && error.text && <Error text={error.text} />}
                    </Fragment>
                  );
                }
              )}
          </EquipmentSectionWrapper>
        )}

        {/* Showing 3Y easy payment fields */}
        {threeYearEasyPaymentFields && (
          <EquipmentSectionWrapper>
            {threeYearEasyPaymentTitle && (
              <EquipmentSectionSubTitle>
                {threeYearEasyPaymentTitle}
              </EquipmentSectionSubTitle>
            )}
            {formFields[name] &&
              Array.isArray(threeYearEasyPaymentFields) &&
              threeYearEasyPaymentFields.map(
                ({ fields, sys: { id: sysId } }, index) => {
                  const key = `EquipmentSection${name}${index}`;
                  const error =
                    formErrors[name] && formErrors[name][fields.name];

                  // Must run BEFORE currentField/hiddenValue are computed so
                  // isVisible reflects this render, not the next one.
                  if (fields && fields.name === easyThreeYearVarifocalCameraVulcan) {
                    if (channelName !== 'SMB Growth Sales CBS' && formFields[name][easyThreeYearVarifocalCameraVulcan]) {
                      formFields[name][easyThreeYearVarifocalCameraVulcan].isVisible = false;
                    }
                  }

                  const currentField = formFields[name][fields.name];
                  const hiddenValue = checkIfHasSwitchOption()
                    ? !currentField ||
                      !currentField.isVisible ||
                      currentField.isHidden
                    : false;

                  const temperatureSensorCount = countTemperatureSensor(
                    'threeYearEasyPayment'
                  );
                  if (
                    currentPath === vulcanForm &&
                    formFields[name][easyThreeYearTemperatureProbeVulcan] &&
                    temperatureSensorCount < 1
                  ) {
                    formFields[name][
                      easyThreeYearTemperatureProbeVulcan
                    ].isVisible = false;
                  }

                  // PowerG Door/Window Sensor (Contact, 3Y): hide unless matching Aux qty > 0
                  if (
                    formFields[name][
                      easyThreeYearPowergDoorWindowSensorVulcan
                    ] &&
                    !(
                      formFields[name][
                        easyThreeYearPowergDoorWindowSensorAuxVulcan
                      ] &&
                      parseInt(
                        formFields[name][
                          easyThreeYearPowergDoorWindowSensorAuxVulcan
                        ].value || 0,
                        10
                      ) > 0
                    )
                  ) {
                    formFields[name][
                      easyThreeYearPowergDoorWindowSensorVulcan
                    ].isVisible = false;
                  }

                  const fieldData = {
                    ...fields,
                    ...formFields[name][fields.name],
                    isHidden: hiddenValue,
                    formFields,
                    sysId,
                    onChange: e => handleFormField(e.target)
                  };
                  return (
                    <Fragment key={key}>
                      <FormField data={fieldData} />
                      {error && error.text && <Error text={error.text} />}
                    </Fragment>
                  );
                }
              )}
          </EquipmentSectionWrapper>
        )}

        {/* Showing 5Y easy payment fields for vulcan */}
        {fiveYearEasyPaymentFields && (
          <EquipmentSectionWrapper>
            {fiveYearEasyPaymentTitle && (
              <EquipmentSectionSubTitle>
                {fiveYearEasyPaymentTitle}
              </EquipmentSectionSubTitle>
            )}
            {formFields[name] &&
              Array.isArray(fiveYearEasyPaymentFields) &&
              fiveYearEasyPaymentFields.map(
                ({ fields, sys: { id: sysId } }, index) => {
                  const key = `EquipmentSection${name}${index}`;
                  const error =
                    formErrors[name] && formErrors[name][fields.name];

                  // Must run BEFORE currentField/hiddenValue are computed so
                  // isVisible reflects this render, not the next one.
                  if (fields && fields.name === easyFiveYearVarifocalCameraVulcan) {
                    if (channelName !== 'SMB Growth Sales CBS' && formFields[name][easyFiveYearVarifocalCameraVulcan]) {
                      formFields[name][easyFiveYearVarifocalCameraVulcan].isVisible = false;
                    }
                  }

                  const currentField = formFields[name][fields.name];
                  const hiddenValue = checkIfHasSwitchOption()
                    ? !currentField ||
                      !currentField.isVisible ||
                      currentField.isHidden
                    : false;

                  const temperatureSensorCount = countTemperatureSensor(
                    'fiveYearEasyPayment'
                  );
                  if (
                    currentPath === vulcanForm &&
                    formFields[name][easyFiveYearTemperatureProbeVulcan] &&
                    temperatureSensorCount < 1
                  ) {
                    formFields[name][
                      easyFiveYearTemperatureProbeVulcan
                    ].isVisible = false;
                  }

                  // PowerG Door/Window Sensor (Contact, 5Y): hide unless matching Aux qty > 0
                  if (
                    formFields[name][
                      easyFiveYearPowergDoorWindowSensorVulcan
                    ] &&
                    !(
                      formFields[name][
                        easyFiveYearPowergDoorWindowSensorAuxVulcan
                      ] &&
                      parseInt(
                        formFields[name][
                          easyFiveYearPowergDoorWindowSensorAuxVulcan
                        ].value || 0,
                        10
                      ) > 0
                    )
                  ) {
                    formFields[name][
                      easyFiveYearPowergDoorWindowSensorVulcan
                    ].isVisible = false;
                  }

                  const fieldData = {
                    ...fields,
                    ...formFields[name][fields.name],
                    isHidden: hiddenValue,
                    formFields,
                    sysId,
                    onChange: e => handleFormField(e.target)
                  };
                  return (
                    <Fragment key={key}>
                      <FormField data={fieldData} />
                      {error && error.text && <Error text={error.text} />}
                    </Fragment>
                  );
                }
              )}
          </EquipmentSectionWrapper>
        )}
        </div>
      </div>
      <Modal
        heading="Alert"
        isOpen={isAlert}
        bodyText={bodyText}
        onClose={() => setIsAlertOpen(false)}
      />
    </>
  );
};

EquipmentSectionForm.propTypes = {
  data: PropTypes.shape({
    formStepFields: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({ name: PropTypes.string }),
        sys: PropTypes.shape({ id: PropTypes.string })
      })
    ),
    easyPaymentFields: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({ name: PropTypes.string }),
        sys: PropTypes.shape({ id: PropTypes.string })
      })
    ),
    title: PropTypes.string,
    subTitle: PropTypes.string,
    maxValue: PropTypes.number,
    isLimitCameraOnly: PropTypes.bool,
    name: PropTypes.string.isRequired,
    oneTimePaymentTitle: PropTypes.string.isRequired,
    oneTimePaymentTitleVulcan: PropTypes.string.isRequired,
    threeYearEasyPaymentTitle: PropTypes.string.isRequired,
    fiveYearEasyPaymentTitle: PropTypes.string.isRequired,
    easyPaymentTitle: PropTypes.string.isRequired,
    oneTimePaymentFields: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({ name: PropTypes.string }),
        sys: PropTypes.shape({ id: PropTypes.string })
      })
    ),
    fiveYearEasyPaymentFields: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({ name: PropTypes.string }),
        sys: PropTypes.shape({ id: PropTypes.string })
      })
    ),
    threeYearEasyPaymentFields: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({ name: PropTypes.string }),
        sys: PropTypes.shape({ id: PropTypes.string })
      })
    )
  }).isRequired,
  formErrors: PropTypes.objectOf(
    PropTypes.objectOf(
      PropTypes.shape({
        text: PropTypes.string
      })
    )
  ),
  formFields: PropTypes.objectOf(
    PropTypes.objectOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        isVisible: PropTypes.bool,
        isHidden: PropTypes.bool,
        fieldLabel: PropTypes.string,
        productType: PropTypes.string,
        minValue: PropTypes.number,
      })
    )
  ).isRequired,
  setFormFields: PropTypes.func.isRequired,
  isFormTouched: PropTypes.bool.isRequired,
  setIsFormTouched: PropTypes.func.isRequired,
  setShowDownloadQuoteFooter: PropTypes.func.isRequired,
  renderTempSensor: PropTypes.bool,
  setRenderTempSensor: PropTypes.func,
  isExpanded: PropTypes.bool,
  channelName: PropTypes.string,
  equipmentTotalPrice: PropTypes.shape({
    subtractAmountFromPrice: PropTypes.number.isRequired,
    addAmountToPrice: PropTypes.number.isRequired,
    addAmountToMonthly: PropTypes.number.isRequired,
    monthlyPayment: PropTypes.number.isRequired
  }).isRequired,
  postalCodeMatches: PropTypes.bool.isRequired
};

export default EquipmentSectionForm;
